<template>
  <div>
    <v-checkbox
      v-model="enabled"
      density="compact"
      hide-details
      class="mt-4 pa-0 i-update-all-zones-checkbox"
      :ripple="false"
      :disabled="disabled"
    >
      <template #label>
        <span
          class="g-bold g-heading-xs g-text-color-black i-update-all-label"
          @click.prevent.stop
        >
          <span v-if="mode === 'panelPricing'"
            >Update Price to All {{ regionLabel }}</span
          >
          <span v-else-if="mode === 'special' || mode === 'priceDrop'"
            >Update Amount Off to All {{ regionLabel }}</span
          >
          <span v-else-if="mode === 'volumeBuy'"
            >Update Volume Buy Fields to All {{ regionLabel }}<br />(once 1st
            tier is added, confirm ALL {{ regionLabel }} with
            <v-icon small color="secondary" class="ml-1">mdi-plus-circle</v-icon
            >. Update ALL 2nd Tier fields & confirm - repeat for 3rd tier)</span
          >

          <v-icon
            class="i-tooltip-color pb-1"
            small
            v-tooltip="`Bulk update pricing values to all ${regionLabelLower}`"
          >
            mdi-help-circle-outline
          </v-icon>
        </span>
      </template>
    </v-checkbox>

    <v-row v-if="enabled && !disabled" class="mt-2">
      <v-col
        v-for="field in fieldConfig.show"
        :key="field"
        :cols="getColSize()"
      >
        <i-numeric-field
          dense
          outlined
          :prefix="fieldPrefix(field)"
          :precision="field === 'cartons' ? 0 : 2"
          :min="0"
          :max="fieldMax(field)"
          hide-details
          :label="fieldConfig.labels[field]"
          :hint="fieldConfig.tooltips[field]"
          persistent-hint
          :suffix="fieldSuffix(field)"
          :empty-zero="field === 'amountOff' && isPercentageDiscountMode"
          :value="fieldModel(field)"
          @input="onInput(field, $event)"
        />
      </v-col>
    </v-row>
    <v-row v-if="enabled && !disabled && mode === 'volumeBuy'" class="mt-2">
      <v-col cols="12" class="text-right">
        <v-btn
          class="i-btn-block-secondary"
          rounded
          small
          @click="$emit('add-all-tiers')"
        >
          Add ALL Tiers
        </v-btn>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import iNumericField from '@/components/NumericField.vue'
import { DISCOUNT_ENTRY_MODE } from '@/modules/dataMaintenance/helper/DiscountEntryHelper'
import { mapGetters } from 'vuex'

export default {
  name: 'UpdateAllZones',
  components: { iNumericField },

  props: {
    mode: {
      type: String,
      required: true,
      validator: (val) =>
        ['panelPricing', 'special', 'priceDrop', 'volumeBuy'].includes(val)
    },
    hideUnitRrp: {
      type: Boolean,
      default: false
    },
    isServiceProduct: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    regionLabel: {
      type: String,
      default: 'Zones'
    },
    discountEntryMode: {
      type: String,
      default: DISCOUNT_ENTRY_MODE.AMOUNT
    }
  },

  data() {
    return {
      enabled: false,
      cartonPrice: null,
      unitRrp: null,
      amountOff: null,
      cartons: null,
      debounceEmitUpdate: {}
    }
  },

  computed: {
    ...mapGetters('tenant', ['tenantCurrency', 'tenantLocale']),

    regionLabelLower() {
      return this.regionLabel.toLowerCase()
    },

    isPercentageDiscountMode() {
      return this.discountEntryMode === DISCOUNT_ENTRY_MODE.PERCENTAGE
    },

    currencySymbol() {
      return (
        new Intl.NumberFormat(this.tenantLocale, {
          style: 'currency',
          currency: this.tenantCurrency,
          minimumFractionDigits: 0,
          maximumFractionDigits: 0
        })
          .formatToParts(0)
          .find((part) => part.type === 'currency')?.value || '$'
      )
    },

    fieldConfig() {
      const map = {
        panelPricing: {
          show: this.hideUnitRrp ? ['cartonPrice'] : ['cartonPrice', 'unitRrp'],
          labels: {
            cartonPrice: this.isServiceProduct ? 'Price' : 'Carton Price',
            unitRrp: 'Unit RRP'
          },
          tooltips: {
            cartonPrice: `Update carton price across all ${this.regionLabelLower}`,
            unitRrp: `Update unit RRP across all ${this.regionLabelLower}`
          }
        },
        special: {
          show: this.hideUnitRrp ? ['amountOff'] : ['amountOff', 'unitRrp'],
          labels: {
            amountOff: this.isServiceProduct
              ? this.amountOffLabel
              : `${this.amountOffLabel} (Carton)`,
            unitRrp: 'Unit RRP'
          },
          tooltips: {
            amountOff: `Apply ${this.amountOffTooltip} to all ${this.regionLabelLower}`,
            unitRrp: `Apply unit RRP to all ${this.regionLabelLower}`
          }
        },
        priceDrop: {
          show: this.hideUnitRrp ? ['amountOff'] : ['amountOff', 'unitRrp'],
          labels: {
            amountOff: this.isServiceProduct
              ? this.amountOffLabel
              : `${this.amountOffLabel} (Carton)`,
            unitRrp: 'Unit RRP'
          },
          tooltips: {
            amountOff: `Apply ${this.amountOffTooltip} to all ${this.regionLabelLower}`,
            unitRrp: `Apply unit RRP to all ${this.regionLabelLower}`
          }
        },
        volumeBuy: {
          show: this.hideUnitRrp
            ? ['cartons', 'amountOff']
            : ['cartons', 'amountOff', 'unitRrp'],
          labels: {
            cartons: this.isServiceProduct
              ? 'No. of Service'
              : 'Number of Cartons',
            amountOff: this.isServiceProduct
              ? this.amountOffLabel
              : `${this.amountOffLabel} (Carton)`,
            unitRrp: 'Unit RRP'
          },
          tooltips: {
            cartons: `Apply quantity threshold to all ${this.regionLabelLower}`,
            amountOff: `Apply ${this.amountOffTooltip}`,
            unitRrp: 'Apply unit RRP'
          }
        }
      }
      return map[this.mode]
    },

    amountOffLabel() {
      return this.isPercentageDiscountMode ? 'Discount %' : 'Amount Off'
    },

    amountOffTooltip() {
      return this.isPercentageDiscountMode
        ? 'discount percentage'
        : 'amount off'
    }
  },

  watch: {
    disabled(disabled) {
      if (disabled) this.resetFields()
    },
    discountEntryMode(newMode, oldMode) {
      if (oldMode && newMode !== oldMode) {
        this.amountOff = null
      }
    }
  },

  methods: {
    fieldModel(field) {
      return this[field]
    },
    emitUpdate(field) {
      const eventMap = {
        cartonPrice: 'update-carton',
        amountOff: this.isPercentageDiscountMode
          ? 'update-discount-percentage'
          : 'update-carton',
        unitRrp: 'update-rrp',
        cartons: 'update-cartons'
      }
      this.$emit(eventMap[field], this[field])
    },
    getColSize() {
      return this.fieldConfig.show.length === 3 ? 4 : 6
    },

    fieldPrefix(field) {
      if (
        field === 'cartons' ||
        (field === 'amountOff' && this.isPercentageDiscountMode)
      ) {
        return ''
      }
      return this.currencySymbol
    },

    fieldSuffix(field) {
      return field === 'amountOff' && this.isPercentageDiscountMode
        ? '%'
        : undefined
    },

    fieldMax(field) {
      if (field === 'cartons') return 999
      if (field === 'amountOff' && this.isPercentageDiscountMode) return 99.99
      return 9999.99
    },

    onInput(field, value) {
      this[field] = value
      // Every emitted update triggers each caller to recalculate pricing across
      // every zone (and re-validate the whole row) - doing that synchronously on
      // every keystroke made typing here feel laggy. Debouncing lets the field
      // itself (bound to `this[field]` above) stay instantly responsive while the
      // expensive downstream recalculation only runs once typing pauses.
      clearTimeout(this.debounceEmitUpdate[field])
      this.debounceEmitUpdate[field] = setTimeout(() => {
        this.emitUpdate(field)
      }, 300)
    },

    resetFields() {
      this.cartonPrice = null
      this.amountOff = null
      this.unitRrp = null
      this.cartons = null
      this.enabled = false
    }
  }
}
</script>

<style scoped lang="scss">
.i-update-all-label {
  display: inline-block;
  line-height: 24px;
  text-align: left;
}

.i-update-all-zones-checkbox :deep(.v-selection-control) {
  align-items: flex-start;
}

.i-update-all-zones-checkbox :deep(.v-selection-control__wrapper) {
  align-self: flex-start;
}

.i-update-all-zones-checkbox :deep(.v-label) {
  align-items: flex-start;
  min-height: 24px;
  opacity: 1;
}
</style>
